import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Users, Plus, Shield, Eye, EyeOff, 
  Heart, MessageCircle, Share2, MoreHorizontal, X, Check,
  BarChart3, Clock, Target, Sparkles, ChevronRight,
  Camera, Mic, Image, Video, FileText, Lock, Globe, UserCheck
} from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import CosmicConnectionsMap from '../components/IdentityCircle/CosmicConnectionsMap';
import IdentityFeed from '../components/IdentityCircle/IdentityFeed';
import StatsSelection from '../components/IdentityCircle/StatsSelection';
import PrivacySelector from '../components/IdentityCircle/PrivacySelector';
import CreateContentModal from '../components/IdentityCircle/CreateContentModal';
import UserDetailModal from '../components/IdentityCircle/UserDetailModal';
import UserProfileModal from '../components/IdentityCircle/UserProfileModal';
import '../styles/identityCircle.css';

const IdentityCircleScreen = () => {
  const { navigateToScreen, isDarkMode } = useUI();
  const { user } = useAuth();
  const { t } = useLanguage();
    // State management
  const [activeTab, setActiveTab] = useState('feed');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStatsSelection, setShowStatsSelection] = useState(false);
  const [selectedStats, setSelectedStats] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [privacy, setPrivacy] = useState('circle');
  const [feedItems, setFeedItems] = useState([]);
  const [connections, setConnections] = useState({
    closeCircle: [],
    generalCircle: [],
    privateGroups: []
  });
  
  // Theme configuration
  const currentTheme = {
    bg: isDarkMode ? 'bg-gray-900' : 'bg-gray-50',
    cardBg: isDarkMode ? 'bg-gray-800' : 'bg-white',
    textPrimary: isDarkMode ? 'text-gray-100' : 'text-gray-800',
    textSecondary: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    textMuted: isDarkMode ? 'text-gray-500' : 'text-gray-400',
    border: isDarkMode ? 'border-gray-700' : 'border-gray-200',
    hover: isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    accent: 'bg-gradient-to-r from-indigo-500 to-purple-500',
    accentHover: 'hover:from-indigo-600 hover:to-purple-600',
    accentLight: isDarkMode ? 'bg-indigo-900/20' : 'bg-indigo-50'
  };

  // Load initial data
  useEffect(() => {
    loadMockData();
  }, []);

  const loadMockData = () => {
    // Mock connections
    setConnections({
      closeCircle: [
        { id: 1, name: 'Sarah Chen', avatar: '👩‍💻', xp: 2450, lastActive: '2m ago' },
        { id: 2, name: 'Alex Kim', avatar: '🧑‍🎨', xp: 1890, lastActive: '5m ago' },
        { id: 3, name: 'Jordan Lee', avatar: '🏃', xp: 3200, lastActive: '1h ago' }
      ],
      generalCircle: [
        { id: 4, name: 'Emma Wilson', avatar: '📚', xp: 1560, lastActive: '3h ago' },
        { id: 5, name: 'Michael Brown', avatar: '🎮', xp: 2100, lastActive: '1d ago' }
      ],      privateGroups: [
        { 
          id: 'g1', 
          name: 'Dev Team', 
          members: 8, 
          icon: '💻',
          membersList: [
            { id: 'm1', name: 'Alice Dev', avatar: '�‍�💻', xp: 2800, level: 12, lastActive: 'Online', isOnline: true },
            { id: 'm2', name: 'Bob Code', avatar: '👨‍💻', xp: 2300, level: 10, lastActive: '1h ago', isOnline: true },
            { id: 'm3', name: 'Charlie JS', avatar: '👨‍🎓', xp: 1900, level: 8, lastActive: '30m ago', isOnline: true },
            { id: 'm4', name: 'Diana React', avatar: '👩‍🔬', xp: 3100, level: 14, lastActive: 'Online', isOnline: true },
            { id: 'm5', name: 'Evan Node', avatar: '👨‍🔧', xp: 2600, level: 11, lastActive: '2h ago', isOnline: false }
          ]
        },
        { 
          id: 'g2', 
          name: 'Fitness Crew', 
          members: 5, 
          icon: '💪',
          membersList: [
            { id: 'f1', name: 'Mike Muscle', avatar: '💪', xp: 2200, level: 9, lastActive: 'Online', isOnline: true },
            { id: 'f2', name: 'Sarah Strong', avatar: '🏋️‍♀️', xp: 2700, level: 12, lastActive: '1h ago', isOnline: true },
            { id: 'f3', name: 'Tom Train', avatar: '🏃‍♂️', xp: 1800, level: 7, lastActive: '3h ago', isOnline: false },
            { id: 'f4', name: 'Lisa Lift', avatar: '🤸‍♀️', xp: 2100, level: 9, lastActive: '2h ago', isOnline: true },
            { id: 'f5', name: 'Jake Jog', avatar: '🚴‍♂️', xp: 1900, level: 8, lastActive: '4h ago', isOnline: false }
          ]
        }
      ]
    });

    // Mock feed items
    setFeedItems([
      {
        id: 1,
        type: 'achievement',
        author: { name: 'Sarah Chen', avatar: '👩‍💻' },
        content: 'Completed 7-Day Meditation Streak! 🧘‍♀️',
        achievement: { name: '7-Day Meditation', icon: '🧘', xp: 150 },
        privacy: 'circle',
        timestamp: '2 hours ago',
        likes: 12,
        comments: 3
      },
      {
        id: 2,
        type: 'stats',
        author: { name: 'Alex Kim', avatar: '🧑‍🎨' },
        content: 'Weekly productivity stats looking good! 📈',
        stats: [
          { label: 'Focus Time', value: '32h', trend: '+12%' },
          { label: 'Tasks Done', value: '47', trend: '+8%' }
        ],
        privacy: 'general',
        timestamp: '4 hours ago',
        likes: 8,
        comments: 2
      },
      {
        id: 3,
        type: 'post',
        author: { name: 'You', avatar: user?.avatar || '🌟' },
        content: 'Just finished building a new feature for the app! Excited to share it with the team soon. #coding #progress',
        privacy: 'selected',
        timestamp: '6 hours ago',
        likes: 24,
        comments: 7
      }
    ]);
  };

  // Simulate XP gain
  const simulateXP = useCallback((amount = 10) => {
    // Animation for XP gain
    const xpElement = document.createElement('div');
    xpElement.className = 'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl font-bold text-green-500 z-50';
    xpElement.textContent = `+${amount} XP`;
    document.body.appendChild(xpElement);
    
    setTimeout(() => {
      xpElement.classList.add('animate-float-up-fade');
      setTimeout(() => xpElement.remove(), 1000);
    }, 100);
  }, []);
  // Handle user interaction
  const handleUserClick = useCallback((user) => {
    console.log('IdentityCircleScreen: handleUserClick called with:', user);
    setSelectedUser(user);
  }, []);

  // Handle navigation
  const handleBack = useCallback(() => {
    navigateToScreen('dashboard');
  }, [navigateToScreen]);

  // Handle content creation
  const handleCreateContent = useCallback((type, privacy = 'circle') => {
    console.log('Creating content:', { type, privacy });
    
    if (type === 'stats') {
      setShowStatsSelection(true);
      setShowCreateModal(false);
    } else {
      // Pass privacy setting to content creation screens
      navigateToScreen(`${type}Creation`, { privacy });
      setShowCreateModal(false);
    }
  }, [navigateToScreen]);

  // Handle stats selection
  const handleStatSelection = useCallback((stat) => {
    setSelectedStats(prev => {
      const exists = prev.find(s => s.id === stat.id);
      if (exists) {
        return prev.filter(s => s.id !== stat.id);
      }
      return [...prev, stat];
    });
  }, []);

  // Handle share stats
  const handleShareStats = useCallback(() => {
    if (selectedStats.length === 0) return;
    
    const newPost = {
      id: Date.now(),
      type: 'stats',
      author: { name: 'You', avatar: user?.avatar || '🌟' },
      content: 'Check out my progress! 📊',
      stats: selectedStats.map(s => ({
        label: s.title,
        value: s.value,
        description: s.description
      })),
      privacy,
      timestamp: 'just now',
      likes: 0,
      comments: 0
    };
    
    setFeedItems([newPost, ...feedItems]);
    setSelectedStats([]);
    setShowStatsSelection(false);
    simulateXP(25);
  }, [selectedStats, privacy, feedItems, user, simulateXP]);

  // Handle like
  const handleLike = useCallback((postId) => {
    setFeedItems(prev => prev.map(item => 
      item.id === postId 
        ? { ...item, likes: item.likes + 1, liked: !item.liked }
        : item
    ));
    simulateXP(5);  }, [simulateXP]);

  // Handle adding connections through QR
  const handleAddConnection = useCallback(() => {
    navigateToScreen('qrScanner');
  }, [navigateToScreen]);

  return (
    <div className={`identity-circle-container ${currentTheme.bg} min-h-screen`}>
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" />
      </div>

      {/* Header */}
      <div className={`identity-circle-header ${currentTheme.cardBg} ${currentTheme.border} border-b`}>        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className={`p-2 rounded-full ${currentTheme.hover} transition-colors`}
            >
              <ArrowLeft size={20} className={currentTheme.textPrimary} />
            </button>
            <div>
              <h1 className={`text-xl font-bold ${currentTheme.textPrimary}`}>
                {t('identityCircle.title', 'IdentityCircle')}
              </h1>
              <p className={`text-sm ${currentTheme.textSecondary}`}>
                {t('identityCircle.subtitle', 'Your trusted social layer')}
              </p>
            </div>
          </div>
        </div>{/* Tabs */}
        <div className="flex space-x-6 px-4 pb-2">
          {['feed', 'connections'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-2 px-1 border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-indigo-500 text-indigo-500'
                  : `border-transparent ${currentTheme.textSecondary}`
              }`}
            >
              {t(`identityCircle.tabs.${tab}`, tab.charAt(0).toUpperCase() + tab.slice(1))}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="identity-circle-content">        <AnimatePresence mode="wait">
          {activeTab === 'feed' && (
            <IdentityFeed 
              currentTheme={currentTheme}
              feedItems={feedItems}
              t={t}
            />
          )}
            {activeTab === 'connections' && (
            <CosmicConnectionsMap              connections={connections}
              currentTheme={currentTheme}
              onUserClick={handleUserClick}
              onAddConnection={handleAddConnection}
              t={t}
              modalOpen={showCreateModal}
            />
          )}
        </AnimatePresence>      </div>

      {/* Modals */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateContentModal
            currentTheme={currentTheme}
            onClose={() => setShowCreateModal(false)}
            onSelect={handleCreateContent}
            t={t}
          />
        )}
        
        {showStatsSelection && (
          <div className="identity-circle-modal">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="identity-circle-modal-content"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${currentTheme.textPrimary}`}>
                  {t('identityCircle.shareStats.title', 'Share Your Stats')}
                </h2>
                <button
                  onClick={() => setShowStatsSelection(false)}
                  className={`p-2 rounded-full ${currentTheme.hover}`}
                >
                  <X size={20} className={currentTheme.textPrimary} />
                </button>
              </div>
              
              <StatsSelection
                currentTheme={currentTheme}
                selectedStats={selectedStats}
                onStatSelection={handleStatSelection}
                onBack={() => setShowStatsSelection(false)}
                onShareStats={handleShareStats}
                t={t}
              />
              
              <div className="mt-6">
                <PrivacySelector
                  currentTheme={currentTheme}
                  privacy={privacy}
                  onPrivacyChange={setPrivacy}
                  t={t}
                />
              </div>
              
              <button
                onClick={handleShareStats}
                disabled={selectedStats.length === 0}
                className={`w-full mt-6 py-3 rounded-xl font-medium transition-all ${
                  selectedStats.length > 0
                    ? `${currentTheme.accent} ${currentTheme.accentHover} text-white`
                    : `${currentTheme.border} ${currentTheme.textMuted} cursor-not-allowed`
                }`}
              >
                {t('identityCircle.shareStats.share', 'Share Stats')}
              </button>
            </motion.div>
          </div>
        )}        
        {selectedUser && (
          <UserProfileModal
            user={selectedUser}
            currentTheme={currentTheme}
            onClose={() => setSelectedUser(null)}
            onMessage={(userId) => {
              navigateToScreen('messaging', { recipient: userId });
              setSelectedUser(null);
            }}
            t={t}
            isVisible={!!selectedUser}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default IdentityCircleScreen;
